/**
 * Scenarios Hook
 *
 * React Query hooks for scenario management.
 * TASK-2026-076: Scenario Persistence
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  scenariosApi,
  Scenario,
  CreateScenarioInput,
  UpdateScenarioInput,
  ScenarioResult,
} from '@/api/client';

/**
 * List all scenarios for a client
 */
export function useScenarios(clientId: string | undefined) {
  return useQuery({
    queryKey: ['scenarios', clientId],
    queryFn: async () => {
      const response = await scenariosApi.list(clientId!);
      return response.scenarios;
    },
    enabled: !!clientId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Get a single scenario
 */
export function useScenario(clientId: string | undefined, scenarioId: string | undefined) {
  return useQuery({
    queryKey: ['scenarios', clientId, scenarioId],
    queryFn: () => scenariosApi.get(clientId!, scenarioId!),
    enabled: !!clientId && !!scenarioId,
    staleTime: 30 * 1000,
  });
}

/**
 * Create a new scenario
 */
export function useCreateScenario(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateScenarioInput) => scenariosApi.create(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId] });
    },
  });
}

/**
 * Update an existing scenario
 */
export function useUpdateScenario(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, data }: { scenarioId: string; data: UpdateScenarioInput }) =>
      scenariosApi.update(clientId, scenarioId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId] });
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId, variables.scenarioId] });
    },
  });
}

/**
 * Delete a scenario
 */
export function useDeleteScenario(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenarioId: string) => scenariosApi.delete(clientId, scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId] });
    },
  });
}

/**
 * Duplicate a scenario
 */
export function useDuplicateScenario(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, name }: { scenarioId: string; name?: string }) =>
      scenariosApi.duplicate(clientId, scenarioId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId] });
    },
  });
}

/**
 * Run a scenario simulation
 */
export function useRunScenario(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenarioId: string) => scenariosApi.run(clientId, scenarioId),
    onSuccess: (_, scenarioId) => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId] });
      queryClient.invalidateQueries({ queryKey: ['scenarios', clientId, scenarioId] });
    },
  });
}

/**
 * List scenario comparisons
 */
export function useScenarioComparisons(clientId: string | undefined) {
  return useQuery({
    queryKey: ['comparisons', clientId],
    queryFn: async () => {
      const response = await scenariosApi.listComparisons(clientId!);
      return response.comparisons;
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

/**
 * Create a comparison
 */
export function useCreateComparison(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; scenarioIds: string[] }) =>
      scenariosApi.createComparison(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comparisons', clientId] });
    },
  });
}
